import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import { VisitTracker } from "@/components/VisitTracker";
import { AutoContrast } from "@/components/AutoContrast";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import DesignDetail from "@/pages/DesignDetail";
import VendorMarket from "@/pages/VendorMarket";
import VendorCheckout from "@/pages/VendorCheckout";
import ServicePage from "@/pages/ServicePage";
import Merchandise from "@/pages/Merchandise";
import CollectionStore from "@/pages/CollectionStore";
import GangSheetBuilder from "@/pages/GangSheetBuilder";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import OrderPlaced from "@/pages/OrderPlaced";
import TrackOrder from "@/pages/TrackOrder";
import Account from "@/pages/Account";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ArtworkGuidelines from "@/pages/ArtworkGuidelines";
import FAQ from "@/pages/FAQ";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Admin from "@/pages/Admin";
import Approval from "@/pages/Approval";
import DraftView from "@/pages/DraftView";
import EstimateView from "@/pages/EstimateView";
import InvoiceView from "@/pages/InvoiceView";
import RequestDesign from "@/pages/RequestDesign";
import OrderLabels from "@/pages/OrderLabels";
import DesignApproval from "@/pages/DesignApproval";
import OrderApproval from "@/pages/OrderApproval";
import PickupBooking from "@/pages/PickupBooking";
import ReviewPage from "@/pages/ReviewPage";
import ReviewsPage from "@/pages/ReviewsPage";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono-p">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function NotFound() {
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <p className="font-mono-p text-[#ff6a13] text-sm uppercase tracking-widest">404</p>
      <h1 className="font-display text-3xl font-extrabold text-white mt-3">Page not found</h1>
      <p className="text-zinc-400 mt-2">The page you're looking for doesn't exist.</p>
      <Link to="/" className="inline-block mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full px-6 py-3 font-semibold active:scale-95 transition-transform">Back home</Link>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/catalog/:designId" element={<DesignDetail />} />
        <Route path="/vendor" element={<VendorMarket />} />
        <Route path="/vendor/design/:designId" element={<DesignDetail vendor />} />
        <Route path="/vendor/checkout" element={<VendorCheckout />} />
        <Route path="/services/:slug" element={<ServicePage />} />
        <Route path="/merch" element={<Merchandise />} />
        <Route path="/collections" element={<CollectionStore />} />
        <Route path="/collections/:collectionId" element={<CollectionStore />} />
        <Route path="/builder" element={<GangSheetBuilder />} />
        <Route path="/request-design" element={<RequestDesign />} />
        <Route path="/order-labels" element={<OrderLabels />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/order-placed" element={<OrderPlaced />} />
        <Route path="/track" element={<TrackOrder />} />
        <Route path="/approve/:token" element={<Approval />} />
        <Route path="/draft/:token" element={<DraftView />} />
        <Route path="/estimate/:token" element={<EstimateView />} />
        <Route path="/invoice/:token" element={<InvoiceView />} />
        <Route path="/design-approval/:token" element={<DesignApproval />} />
        <Route path="/order-approval/:token" element={<OrderApproval />} />
        <Route path="/pickup/:token" element={<PickupBooking />} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/artwork-guidelines" element={<ArtworkGuidelines />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Protected><Account /></Protected>} />
        <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-right" richColors theme="dark" />
          <VisitTracker />
          <AutoContrast />
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
